import { z } from 'zod';
import { languageCodeSchema } from './facility';

// Shape of a single language entry inside the products.product.title or
// .description JSONB array. Mirrors legacy ProductTitleDto /
// ProductDescriptionDto from travel_planner_back. The `default` flag is
// only meaningful on title entries; description entries omit it.
export const productTranslationEntrySchema = z.object({
  languageCode: languageCodeSchema,
  text: z.string(),
  default: z.boolean().optional(),
});

export type ProductTranslationEntry = z.infer<
  typeof productTranslationEntrySchema
>;

export const productFailureSummarySchema = z.object({
  productId: z.uuid(),
  reason: z.string(),
});

export type ProductFailureSummary = z.infer<typeof productFailureSummarySchema>;

const distinctLanguagePair = (s: {
  sourceLanguage: string;
  targetLanguage: string;
}) => s.sourceLanguage !== s.targetLanguage;

const distinctLanguagePairMessage =
  'sourceLanguage must differ from targetLanguage';

export const catalogTranslatePreviewInput = z
  .object({
    catalogId: z.uuid(),
    sourceLanguage: languageCodeSchema,
    targetLanguage: languageCodeSchema,
  })
  .refine(distinctLanguagePair, { message: distinctLanguagePairMessage });

export type CatalogTranslatePreviewInput = z.infer<
  typeof catalogTranslatePreviewInput
>;

export const catalogTranslatePreviewResult = z.object({
  sourceCount: z.number().int().nonnegative(),
  targetCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
});

export type CatalogTranslatePreviewResult = z.infer<
  typeof catalogTranslatePreviewResult
>;

// Server cap. Higher values can be supported once the async Phase-2
// port lands (BullMQ + SSE). Sync-first the budget is per-request
// HTTP wall-clock and Google Translate v2 latency.
export const CATALOG_TRANSLATE_MAX_PRODUCTS = 100;

export const catalogTranslateInput = z
  .object({
    catalogId: z.uuid(),
    sourceLanguage: languageCodeSchema,
    targetLanguage: languageCodeSchema,
    skipExisting: z.boolean().default(true),
    // Optional user-facing cap on how many products a run translates. When
    // omitted, the run translates ALL eligible products (still bounded by the
    // server ceiling `CATALOG_TRANSLATE_MAX_PRODUCTS` while translation is
    // synchronous). When set, only that many products are translated.
    limit: z
      .number()
      .int()
      .min(1)
      .max(CATALOG_TRANSLATE_MAX_PRODUCTS)
      .optional(),
    // Deprecated alias retained for back-compat with existing callers/tests.
    // The service prefers `limit` when both are present; otherwise this still
    // bounds the run. Either way the server ceiling applies.
    maxProducts: z
      .number()
      .int()
      .min(1)
      .max(CATALOG_TRANSLATE_MAX_PRODUCTS)
      .optional(),
  })
  .refine(distinctLanguagePair, { message: distinctLanguagePairMessage });

export type CatalogTranslateInput = z.infer<typeof catalogTranslateInput>;

export const catalogTranslateResult = z.object({
  translatedCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  failed: z.array(productFailureSummarySchema),
});

export type CatalogTranslateResult = z.infer<typeof catalogTranslateResult>;

// Single in-memory translation of one product's title + description, from
// the source-language text the edit form already holds (no DB read) into a
// newly-added language. Powers the per-language "translate from" control on
// the Title & Description tab. `title` is plain text, `description` is HTML.
export const translateTextInput = z
  .object({
    sourceLanguage: languageCodeSchema,
    targetLanguage: languageCodeSchema,
    title: z.string(),
    description: z.string(),
  })
  .refine(distinctLanguagePair, { message: distinctLanguagePairMessage });

export type TranslateTextInput = z.infer<typeof translateTextInput>;

export const translateTextResult = z.object({
  title: z.string(),
  description: z.string(),
});

export type TranslateTextResult = z.infer<typeof translateTextResult>;
